/**
 * Validation Utilities
 *
 * Centralized validation functions for common data types.
 * These utilities can be used standalone or with form libraries like Yup.
 *
 * @module shared/utils/validation
 */

// ============================================================================
// String & Text Validation
// ============================================================================

/**
 * Checks if a string contains only digits
 *
 * @param str - The string to check
 * @returns `true` if string contains only digits
 *
 * @example
 * isNumberString('12345'); // true
 * isNumberString('12a45'); // false
 */
export const isNumberString = (str: string): boolean => {
  return /^\d+$/.test(str);
};

/**
 * Checks if a string contains potentially dangerous characters
 * commonly used in SQL Injection or XSS attacks
 *
 * @param value - The input string to validate
 * @returns `true` if the string contains invalid characters
 *
 * @example
 * hasInvalidChars("normal text"); // false
 * hasInvalidChars("<script>alert('xss')</script>"); // true
 */
export const hasInvalidChars = (value: string): boolean => {
  const forbiddenPattern = /['";<>/=]|--/;
  return forbiddenPattern.test(value);
};

/**
 * Checks if a string contains only letters and spaces
 *
 * @param value - The string to check
 * @param allowAccents - Allow accented characters (default: true)
 * @returns `true` if string contains only letters and spaces
 *
 * @example
 * isAlphaString('John Doe'); // true
 * isAlphaString('John123'); // false
 * isAlphaString('José García', true); // true
 */
export const isAlphaString = (value: string, allowAccents: boolean = true): boolean => {
  const pattern = allowAccents ? /^[A-Za-záéíóúÁÉÍÓÚñÑüÜ\s]*$/ : /^[A-Za-z\s]*$/;
  return pattern.test(value);
};

// ============================================================================
// Email Validation
// ============================================================================

const EMAIL_MAX_LENGTH = 254;
const EMAIL_LOCAL_MAX_LENGTH = 64;

export const isValidEmail = (email: string): boolean => {
  if (email == null || email.length === 0 || email.length > EMAIL_MAX_LENGTH) {
    return false;
  }
  const at = email.indexOf('@');
  if (at <= 0 || at !== email.lastIndexOf('@')) {
    return false;
  }
  for (let i = 0; i < email.length; i++) {
    const code = email.codePointAt(i);
    if (code && code <= 32) {
      return false;
    }
  }
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.length === 0 || local.length > EMAIL_LOCAL_MAX_LENGTH || domain.length === 0) {
    return false;
  }
  const dot = domain.indexOf('.');
  if (dot <= 0 || dot === domain.length - 1) {
    return false;
  }
  return true;
};

const LOCAL_STRICT_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789._%+-';
const DOMAIN_STRICT_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-';

function segmentOnlyUsesChars(segment: string, allowed: string): boolean {
  for (const char of segment) {
    if (!allowed.includes(char)) {
      return false;
    }
  }
  return true;
}

function tldIsAlphaOnly(tld: string): boolean {
  for (let i = 0; i < tld.length; i++) {
    const c = tld.codePointAt(i);
    if (c && (c < 65 || (c > 90 && c < 97) || c > 122)) {
      return false;
    }
  }
  return tld.length >= 2;
}

/**
 * Email más estricto (equivalente al patrón alfanumérico + dominio + TLD letras).
 * Sin regex con backtracking sobre entradas largas.
 */
export const isValidEmailStrict = (email: string): boolean => {
  if (email == null || email.length === 0 || email.length > EMAIL_MAX_LENGTH) {
    return false;
  }
  const at = email.indexOf('@');
  if (at <= 0 || at !== email.lastIndexOf('@')) {
    return false;
  }
  const local = email.slice(0, at);
  const rest = email.slice(at + 1);
  const lastDot = rest.lastIndexOf('.');
  if (lastDot <= 0 || lastDot >= rest.length - 1) {
    return false;
  }
  const domainBody = rest.slice(0, lastDot);
  const tld = rest.slice(lastDot + 1);
  if (!segmentOnlyUsesChars(local, LOCAL_STRICT_CHARS)) {
    return false;
  }
  if (
    !segmentOnlyUsesChars(domainBody, DOMAIN_STRICT_CHARS) ||
    domainBody.endsWith('.') ||
    domainBody.startsWith('.')
  ) {
    return false;
  }
  if (domainBody.includes('..')) {
    return false;
  }
  return tldIsAlphaOnly(tld);
};

// ============================================================================
// Phone Number Validation
// ============================================================================

/**
 * Validates Bolivian phone number (8 digits)
 *
 * @param phone - The phone number to validate (string or number)
 * @returns `true` if phone number is valid (8 digits)
 *
 * @example
 * isValidPhoneBO('70123456'); // true
 * isValidPhoneBO('7012345'); // false (7 digits)
 * isValidPhoneBO('701234567'); // false (9 digits)
 */
export const isValidPhoneBO = (phone: string | number): boolean => {
  const phoneStr = phone.toString().trim();
  return /^\d{8}$/.test(phoneStr);
};

/**
 * Validates phone number with minimum length
 *
 * @param phone - The phone number to validate
 * @param minLength - Minimum length (default: 8)
 * @returns `true` if phone number meets minimum length
 */
export const isValidPhone = (phone: string | number, minLength: number = 8): boolean => {
  const phoneStr = phone.toString().trim();
  return phoneStr.length >= minLength && /^\d+$/.test(phoneStr);
};

// ============================================================================
// Document Validation (CI - Cédula de Identidad)
// ============================================================================

/**
 * Validates Bolivian CI (Cédula de Identidad) format
 * Accepts: 6-8 digits, optionally followed by complement (e.g., "1234567-1A")
 *
 * @param ci - The CI to validate
 * @returns `true` if CI format is valid
 *
 * @example
 * isValidCI('1234567'); // true
 * isValidCI('1234567-1A'); // true
 * isValidCI('12345'); // false (too short)
 */
export const isValidCI = (ci: string): boolean => {
  return /^\d{6,8}(-[A-Za-z\d]{1,3})?$/.test(ci);
};

/**
 * Validates simple CI format (digits only)
 *
 * @param ci - The CI to validate
 * @param minLength - Minimum length (default: 5)
 * @param maxLength - Maximum length (default: 20)
 * @returns `true` if CI is valid
 */
export const isValidCISimple = (ci: string, minLength: number = 5, maxLength: number = 20): boolean => {
  return /^\d*$/.test(ci) && ci.length >= minLength && ci.length <= maxLength;
};

// ============================================================================
// Password Validation
// ============================================================================

/**
 * Validates password strength
 *
 * @param password - The password to validate
 * @param options - Validation options
 * @returns Object with validation result and message
 *
 * @example
 * isValidPassword('Abc123!@');
 * // { valid: true, message: 'Password is strong' }
 */
export interface PasswordValidationOptions {
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumbers?: boolean;
  requireSpecialChars?: boolean;
}

export interface PasswordValidationResult {
  valid: boolean;
  message: string;
  strength?: 'weak' | 'medium' | 'strong';
}

export const isValidPassword = (
  password: string,
  options: PasswordValidationOptions = {}
): PasswordValidationResult => {
  const {
    minLength = 8,
    requireUppercase = false,
    requireLowercase = false,
    requireNumbers = false,
    requireSpecialChars = false,
  } = options;

  if (password.length < minLength) {
    return {
      valid: false,
      message: `Password must be at least ${minLength} characters long`,
    };
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one uppercase letter',
    };
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one lowercase letter',
    };
  }

  if (requireNumbers && !/\d/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one number',
    };
  }

  if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one special character',
    };
  }

  // Determine strength
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const criteriaCount = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;

  if (password.length >= 12 && criteriaCount >= 3) {
    strength = 'strong';
  } else if (password.length >= 8 && criteriaCount >= 2) {
    strength = 'medium';
  }

  return {
    valid: true,
    message: 'Password is valid',
    strength,
  };
};

// ============================================================================
// Image/File Validation
// ============================================================================

/**
 * Validates image file size
 *
 * @param file - The file to validate (File object or FileList)
 * @param maxSizeMB - Maximum size in megabytes (default: 1)
 * @returns `true` if file size is within limit
 *
 * @example
 * isValidImageSize(file, 2); // Max 2 MB
 */
export const isValidImageSize = (file: File | FileList | null, maxSizeMB: number = 1): boolean => {
  if (!file) {
    return true; // Allow null/undefined for optional fields
  }

  const actualFile = file instanceof FileList ? file[0] : file;
  if (!actualFile) {
    return true;
  }

  const fileSizeMB = actualFile.size / 1024 / 1024;
  return fileSizeMB <= maxSizeMB;
};

/**
 * Validates image file format
 *
 * @param file - The file to validate
 * @param allowedFormats - Array of allowed MIME types
 * @returns `true` if file format is allowed
 *
 * @example
 * isValidImageFormat(file, ['image/jpeg', 'image/png']);
 */
export const isValidImageFormat = (
  file: File | FileList | null,
  allowedFormats: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
): boolean => {
  if (!file) {
    return true;
  }

  const actualFile = file instanceof FileList ? file[0] : file;
  if (!actualFile) {
    return true;
  }

  return allowedFormats.includes(actualFile.type);
};

// ============================================================================
// Numeric Validation
// ============================================================================

/**
 * Validates if a value is within a numeric range
 *
 * @param value - The value to validate
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns `true` if value is within range
 *
 * @example
 * isInRange(50, 1, 100); // true
 * isInRange(150, 1, 100); // false
 */
export const isInRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max;
};

/**
 * Validates if a value is a positive number
 *
 * @param value - The value to validate
 * @returns `true` if value is positive
 */
export const isPositiveNumber = (value: number): boolean => {
  return !Number.isNaN(value) && value > 0;
};

export const toFiniteNumber = (value: unknown, defaultValue: number = 0): number => {
  if (value == null) return defaultValue;
  const n = Number(value);
  return Number.isFinite(n) ? n : defaultValue;
};

export const sanitizeNumericInput = (value: string, maxLength: number): string => {
  const normalizedValue = String(value ?? '');
  return normalizedValue.replace(/\D/g, '').slice(0, maxLength);
};

export const sanitizeAlphanumericUpperInput = (value: string, maxLength: number): string => {
  const normalizedValue = String(value ?? '');
  return normalizedValue
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, maxLength);
};

// ============================================================================
// OTP Validation
// ============================================================================

/**
 * Validates OTP (One-Time Password) code
 *
 * @param otp - The OTP code to validate
 * @param length - Expected length (default: 6)
 * @returns `true` if OTP format is valid
 *
 * @example
 * isValidOTP('123456'); // true
 * isValidOTP('12345'); // false (wrong length)
 * isValidOTP('12a456'); // false (contains letters)
 */
export const isValidOTP = (otp: string, length: number = 6): boolean => {
  const regex = new RegExp(String.raw`^\d{${length}}$`);
  return regex.test(otp);
};

// ============================================================================
// URL Validation
// ============================================================================

/**
 * Validates URL format
 *
 * @param url - The URL to validate
 * @returns `true` if URL format is valid
 *
 * @example
 * isValidURL('https://example.com'); // true
 * isValidURL('invalid-url'); // false
 */
export const isValidURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ============================================================================
// Sanitization Helpers
// ============================================================================

/**
 * Removes dangerous characters from input
 *
 * @param value - The string to sanitize
 * @returns Sanitized string
 *
 * @example
 * sanitizeInput("<script>alert('xss')</script>");
 * // "scriptalert('xss')/script"
 */
export const sanitizeInput = (value: string): string => {
  return value.replace(/['";<>/=]/g, '').replace(/--/g, '');
};

/**
 * Extracts only numeric characters from a string
 *
 * @param value - The string to process
 * @returns String containing only digits
 *
 * @example
 * extractNumbers('Phone: +591 70123456'); // '59170123456'
 */
export const extractNumbers = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Trims and normalizes whitespace in a string
 *
 * @param value - The string to normalize
 * @returns Normalized string
 *
 * @example
 * normalizeWhitespace('  Hello   World  '); // 'Hello World'
 */
export const normalizeWhitespace = (value: string): string => {
  return value.trim().replace(/\s+/g, ' ');
};

// ============================================================================
// Export all validators
// ============================================================================

export const validators = {
  // String validation
  isNumberString,
  hasInvalidChars,
  isAlphaString,

  // Email validation
  isValidEmail,
  isValidEmailStrict,

  // Phone validation
  isValidPhoneBO,
  isValidPhone,

  // Document validation
  isValidCI,
  isValidCISimple,

  // Password validation
  isValidPassword,

  // File validation
  isValidImageSize,
  isValidImageFormat,

  // Numeric validation
  isInRange,
  isPositiveNumber,
  toFiniteNumber,
  sanitizeNumericInput,
  sanitizeAlphanumericUpperInput,

  // OTP validation
  isValidOTP,

  // URL validation
  isValidURL,

  // Sanitization
  sanitizeInput,
  extractNumbers,
  normalizeWhitespace,
};

export default validators;
